import { z } from 'zod'
import { labelDimensions } from '../labels/schema'

/**
 * Tier Rating (#1813) — the platform port of the toolkit `tier-rating` module
 * (includes/TierRating/Settings.php): an operator-ordered ladder of taxonomy
 * terms, each row a tier with a label, 0-5 stars, optional badge art, a tint
 * and a description. A home's tier is the FIRST row (highest rank) whose term
 * it carries (`Settings::match`); a home with no mapped term gets no badge.
 *
 * Toolkit data model: one site option, `taxonomy` (default `property_tag`) +
 * `tiers[]` keyed by WP term id. Here the ladder rides the explorer widget's
 * own settings as `card.tierRating` (optional, never materialized, so stored
 * widgets are byte-identical) and rows are keyed the Label Designer way: a
 * `filterKey` dimension + the NUMERIC Central term id (the wire carries
 * `tags` / `groups` / … as `{id,name}[]`, central#2615, no slugs). Per-row
 * dimension is a superset of the toolkit's single taxonomy and lets the
 * dashboard reuse the chip dimension / term pickers verbatim.
 *
 * Every leaf is lenient at the render boundary: the strict row schema lives
 * here, the row-dropping wrapper (safeParse) is built in config.ts, the
 * sanctioned location per the render-boundary lint (labelOverrides precedent).
 */
export const TIER_ROWS_MAX = 10
export const TIER_STARS_MAX = 5
export const TIER_LABEL_MAX = 40
export const TIER_TEXT_MAX = 600

/** What the badge shows: label + visual, the label alone, the stars alone, or the art alone. */
export const tierDisplays = ['both', 'badge', 'stars', 'image'] as const
export type TierDisplay = (typeof tierDisplays)[number]
/** Where the badge sits on a card: under the title, or top-left over the photo. */
export const tierPlacements = ['title', 'media'] as const
export type TierPlacement = (typeof tierPlacements)[number]

const HEX_COLOR_RE = /^#(?:[0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i
const HTTPS_URL_RE = /^https:\/\/[^\s"'<>]+$/i

// Hand-rolled gates (NOT safeParse — render-boundary lint): normalize an
// authored value to a valid one or to undefined, so the strict inner schema
// never sees a rejectable value on an optional leaf.
const gateNumber = (v: unknown): number | undefined => {
  if (typeof v === 'number') return Number.isFinite(v) ? v : undefined
  if (typeof v === 'string' && v.trim() !== '') { const n = Number(v); return Number.isFinite(n) ? n : undefined }
  return undefined
}
const optText = (max: number) => z.preprocess(
  (v) => (typeof v === 'string' && v.trim() !== '' ? v.trim().slice(0, max) : undefined),
  z.string().max(max).optional(),
)
const optPattern = (re: RegExp, max: number) => z.preprocess(
  (v) => (typeof v === 'string' && re.test(v.trim()) && v.trim().length <= max ? v.trim() : undefined),
  z.string().max(max).optional(),
)

export const tierRowSchema = z.object({
  filterKey: z.enum(labelDimensions).describe('Taxonomy dimension of the term'),
  // gateNumber (not z.coerce): a non-numeric must FAIL the row so the config.ts
  // lenient wrapper drops it, exactly like a label override row.
  centralId: z.preprocess((v) => gateNumber(v) ?? v, z.number().int().positive()).describe('Resolved Central term ID'),
  label: optText(TIER_LABEL_MAX).describe(`Badge label (max ${TIER_LABEL_MAX}). Empty = the term name`),
  stars: z.preprocess(
    (v) => { const n = gateNumber(v); return n === undefined ? undefined : Math.max(0, Math.min(TIER_STARS_MAX, Math.trunc(n))) },
    z.number().int().min(0).max(TIER_STARS_MAX).optional(),
  ).describe(`Stars shown on the badge, 0 to ${TIER_STARS_MAX}. 0 = none`),
  imageUrl: optPattern(HTTPS_URL_RE, 600).describe('Badge art (https). With display "image" the art alone is the badge'),
  color: optPattern(HEX_COLOR_RE, 12).describe('Badge tint (hex). Empty = the neutral badge; text flips to black or white for contrast'),
  description: optText(TIER_TEXT_MAX).describe(`What this tier means (max ${TIER_TEXT_MAX}); shown as the badge tooltip`),
})
export type TierRow = z.infer<typeof tierRowSchema>

const objectOrUndef = (v: unknown) => (typeof v === 'object' && v !== null && !Array.isArray(v) ? v : undefined)

/**
 * Popover (#1821, toolkit `popover` settings): the badge becomes a disclosure
 * button (`tier-rating-frontend.js`): hover and focus open a preview, click or
 * tap pins it, with the operator's "about our ratings" copy and link under the
 * tier name and description. Toolkit default is ON, so `enabled` is only read
 * as the explicit opt-out; the whole group is optional and never materialized.
 */
export const TIER_POPOVER_TITLE_MAX = 80
// https, or a site-relative path (`/about-our-ratings`); never a scheme the
// badge could be made to run (`javascript:`), never protocol-relative.
const SAFE_LINK_RE = /^(?:https:\/\/|\/(?!\/))[^\s"'<>]+$/i
const optBoolean = z.preprocess((v) => (typeof v === 'boolean' ? v : undefined), z.boolean().optional())

export const tierPopoverSchema = z.preprocess(objectOrUndef, z.object({
  enabled: optBoolean.describe('Open a popover from the badge on hover, focus, click or tap. Empty = on (toolkit default); off = the static badge with the description as its tooltip'),
  // `heading`, not `title`: the dashboard form reserves `title` for a panel's own caption.
  heading: optText(TIER_POPOVER_TITLE_MAX).describe(`Small heading above the tier name, e.g. "Our rating system" (max ${TIER_POPOVER_TITLE_MAX})`),
  intro: optText(TIER_TEXT_MAX).describe(`"About our ratings" copy shown under the tier description (max ${TIER_TEXT_MAX})`),
  linkText: optText(TIER_POPOVER_TITLE_MAX).describe('Link text. Empty = "Learn about our ratings"'),
  linkUrl: optPattern(SAFE_LINK_RE, 600).describe('Link target: an https URL or a site path such as /about-our-ratings. Empty = no link'),
}).optional())
export type TierPopoverSettings = NonNullable<z.infer<typeof tierPopoverSchema>>

/**
 * The strict ladder. Order = rank (index 0 is the highest tier). Optional on
 * the card so an unconfigured explorer carries no key at all.
 */
export const tierRatingSchema = z.preprocess(objectOrUndef, z.object({
  tiers: z.array(tierRowSchema).max(TIER_ROWS_MAX).optional()
    .describe(`Ordered tier ladder (max ${TIER_ROWS_MAX}); the first row is the top tier. A home gets the first row whose term it carries`),
  display: z.preprocess(
    (v) => ((tierDisplays as readonly string[]).includes(v as string) ? v : undefined),
    z.enum(tierDisplays).optional(),
  ).describe('What the badge shows. Empty = label and stars (or art). "image" = the art alone, for art that carries the tier name; a tier without art falls back to the full badge'),
  placement: z.preprocess(
    (v) => ((tierPlacements as readonly string[]).includes(v as string) ? v : undefined),
    z.enum(tierPlacements).optional(),
  ).describe('Where the badge sits on a card. Empty = under the title; "media" = top-left over the photo (toolkit: placement)'),
  popover: tierPopoverSchema.describe('Badge popover (toolkit popover): hover/click disclosure with the "about our ratings" copy and link'),
// `.optional()` inside the preprocess: ZodOptional short-circuits on the RAW
// input, so a gate returning undefined would otherwise hit a required object.
}).optional())
export type TierRatingConfig = NonNullable<z.infer<typeof tierRatingSchema>>
